import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  TextField,
  Typography,
} from '@mui/material';

import type { TravelerResponseDto } from '@bandido/shared/dtos';
import type { RuteroDeclarablePassengerField } from '@bandido/shared/types';

import { passengerFieldCells } from '../passengerFields';
import type { DeclaredPassengerField } from '../passengerFields';
import type { RuteroPassengerPayload } from '../queries';
import { PassengerFieldControl } from './PassengerFieldControl';
import { PassengerFieldSlot } from './PassengerFieldSlot';

interface Props {
  /** Quién es esta persona en la reserva: "Pasajero 2", "Conductor". */
  roleLabel: string;
  pax: RuteroPassengerPayload;
  /** La edad que comiteó la búsqueda, si esa búsqueda la pide. */
  age?: number;
  /** Si es falso, la cantidad no autoriza a afirmar una categoría tarifaria. */
  classificationKnown?: boolean;
  /** Lo que la oferta declara para CADA pasajero. */
  fields: DeclaredPassengerField[];
  /**
   * Los que en ESTA fila dibuja un bloque propio (los vuelos del titular, que
   * viven junto al horario de su tramo). Se suprimen por campo Y por fila: lo
   * que el bloque especial cubre para el titular, los acompañantes lo siguen
   * necesitando acá.
   */
  renderedApart?: ReadonlySet<RuteroDeclarablePassengerField>;
  columns: { xs: number; sm: number };
  fieldError: (field: RuteroDeclarablePassengerField) => boolean;
  onChange: (partial: Partial<RuteroPassengerPayload>) => void;
  defaultCountry?: string;
  /**
   * En el paso de quote de Tours la misma fila pide solo la fecha de
   * nacimiento que la sesión exige. Nombre y apellido aparecen recién al
   * avanzar al hold, sin crear un segundo formulario paralelo.
   */
  showIdentityFields?: boolean;
  travelers?: TravelerResponseDto[];
  selectedTraveler?: TravelerResponseDto | null;
  travelerDisabled?: (traveler: TravelerResponseDto) => boolean;
  onTravelerChange?: (traveler: TravelerResponseDto | null) => void;
  canChooseContact?: boolean;
  isContact?: boolean;
  onChooseContact?: () => void;
  disabled?: boolean;
  identityDocuments?: boolean;
}

/**
 * Una fila de pasajero. UNA sola, para el titular y para los acompañantes.
 *
 * Antes eran dos bloques de JSX con anchos distintos (`holderColumns` vs
 * `paxColumns`), así que no alineaban en ningún vertical, y la del titular
 * mezclaba lo que se le pide a TODOS con lo que se le pide a él como contacto.
 * Ahora la fila dibuja siempre lo mismo —rol, nombre, apellido y lo que la
 * oferta declara para todos— y lo del contacto vive en su propio bloque
 * (#2374).
 *
 * El TIPO no se elige: cuando el pedido lo conoce se muestra en gris con la
 * edad al lado. Si el search solo conoce una cantidad, la fila dice pasajero
 * y no presenta el valor técnico del hold como una categoría comercial.
 */
export function PassengerRow({
  roleLabel,
  pax,
  age,
  classificationKnown = true,
  fields,
  renderedApart,
  columns,
  fieldError,
  onChange,
  defaultCountry,
  showIdentityFields = true,
  travelers = [],
  selectedTraveler = null,
  travelerDisabled,
  onTravelerChange,
  canChooseContact = false,
  isContact = false,
  onChooseContact,
  disabled = false,
  identityDocuments = false,
}: Props) {
  const { t } = useTranslation();
  const typeLabel = classificationKnown
    ? t(`travelServices.booking.${pax.type}`)
    : t('travelServices.booking.passenger');

  return (
    <Grid container spacing={1.5} alignItems="flex-start">
      {onTravelerChange && (
        <Grid item xs={12}>
          <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            <Autocomplete
              size="small"
              sx={{ minWidth: 280, flex: '1 1 280px' }}
              options={travelers}
              disabled={disabled}
              value={selectedTraveler}
              getOptionDisabled={(option) => travelerDisabled?.(option) ?? false}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, value) => onTravelerChange(value)}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.birthDate
                        ? t('travelers.birthDateValue', { date: option.birthDate })
                        : t('travelServices.booking.rosterBirthDateMissing')}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('travelServices.booking.rosterTraveler')}
                  helperText={t('travelServices.booking.rosterTravelerHint')}
                />
              )}
            />
            {canChooseContact && (
              <FormControlLabel
                control={(
                  <Radio
                    checked={isContact}
                    disabled={disabled}
                    onChange={onChooseContact}
                    size="small"
                  />
                )}
                label={t('travelServices.booking.bookingContact')}
              />
            )}
          </Box>
        </Grid>
      )}
      <Grid item {...columns}>
        <PassengerFieldSlot>
          <TextField
            select
            size="small"
            fullWidth
            disabled
            label={roleLabel}
            value={pax.type}
          >
            <MenuItem value={pax.type}>
              {age === undefined
                ? typeLabel
                : t('travelServices.booking.paxTypeWithAge', {
                  type: typeLabel,
                  age,
                })}
            </MenuItem>
          </TextField>
        </PassengerFieldSlot>
      </Grid>
      {/* Nombre y apellido no los declara ninguna oferta: los exige SIEMPRE el
          contrato base, así que llevan el badge del nivel que bloquea
          reservar, como cualquier otro campo. En el quote progresivo todavía
          no se muestran: no son necesarios para consultar disponibilidad. */}
      {showIdentityFields && (
        <>
          <Grid item {...columns}>
            <PassengerFieldSlot level="hold">
              <TextField
                size="small"
                fullWidth
                required
                label={t('travelServices.booking.firstName')}
                value={pax.firstName}
                disabled={disabled}
                onChange={(event) => onChange({ firstName: event.target.value })}
              />
            </PassengerFieldSlot>
          </Grid>
          <Grid item {...columns}>
            <PassengerFieldSlot level="hold">
              <TextField
                size="small"
                fullWidth
                required
                label={t('travelServices.booking.lastName')}
                value={pax.lastName}
                disabled={disabled}
                onChange={(event) => onChange({ lastName: event.target.value })}
              />
            </PassengerFieldSlot>
          </Grid>
        </>
      )}
      {fields
        .filter((entry) => !renderedApart?.has(entry.field))
        .map(({ field, level }) => (
          <Grid
            item
            {...columns}
            sm={columns.sm * passengerFieldCells(field)}
            key={field}
          >
            <PassengerFieldControl
              identityDocument={identityDocuments ? { passenger: pax, traveler: selectedTraveler, onChange } : undefined}
              field={field}
              level={level}
              error={fieldError(field)}
              value={pax[field]}
              onChange={(value) => onChange({ [field]: value })}
              defaultCountry={defaultCountry}
              disabled={disabled}
            />
          </Grid>
        ))}
    </Grid>
  );
}
